import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Truncated } from '@frontend/design-system';
import { StatItem, StatItemData } from './stat-item';

type DataOrChildren =
  | {
      data: StatItemData[];
      children?: never;
    }
  | {
      data?: never;
      children: ReactNode;
    };

export type StatsMetricProps = DataOrChildren & {
  dataPointsStyle?: React.CSSProperties;
  isLoading?: boolean;
  maxWidth?: string | number;
  omit?: boolean;
  title: string;
  uniqueId: string;
};

export const StatsMetric = ({
  children,
  data,
  dataPointsStyle,
  isLoading,
  maxWidth,
  omit,
  title,
  uniqueId,
}: StatsMetricProps) => {
  const { isSmallMode } = useIsSmallMode();

  if (omit) {
    return null;
  }

  return (
    <div css={styles.wrapper} data-testid='stats-metric' style={{ maxWidth }}>
      <div css={styles.title}>
        <Truncated
          as='span'
          className='name'
          style={{
            fontSize: isSmallMode ? theme.font.size.large : theme.font.size.h3,
          }}
        >
          {title}
        </Truncated>
      </div>
      <div css={styles.dataPoints} style={dataPointsStyle}>
        {children ??
          data?.map(({ label, isLoading: isLoadingItem, ...rest }) => (
            <StatItem
              isLoading={isLoadingItem || isLoading}
              key={`${uniqueId}-${label}`}
              label={label}
              style={{
                maxWidth: `calc(100% / ${data.length} - ${theme.spacing(3)})`,
              }}
              {...rest}
            />
          ))}
      </div>
    </div>
  );
};

const styles = {
  wrapper: css`
    overflow: hidden;
    position: relative;
  `,

  title: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(2)};
    justify-content: space-between;

    .name {
      font-weight: ${theme.font.weight.bold};
      text-align: start;
    }
  `,

  dataPoints: css`
    column-gap: ${theme.spacing(1)};
    display: flex;
    margin-top: ${theme.spacing(1)};
    justify-content: space-between;
  `,
};
